import { Role } from '@customTypes/types';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { PhotoProvider } from 'react-photo-view';

import { ErrorPage, Loading, NotFoundPage } from '@/pages';
import { routeTree } from '@/routeTree.gen';
import {
  ARTIST_THEME,
  CLUB_THEME,
  DEFAULT_THEME
} from '@/shared/constants/mantine-theme';
import { useLocaleContext } from '@/shared/contexts';
import { messages } from '@/shared/i18n/messages';
import { RootLayout } from '@/shared/layouts';

import 'react-photo-view/dist/react-photo-view.css';
import { getProfiles } from './shared/api/requests';
import { APP_CONST } from './shared/constants';
import { usePushNotifications } from './shared/hooks/usePushNotifications';
import { LOCALES } from './shared/i18n/locales';
import { useProfileStore } from './shared/store/profileStore';

const router = createRouter({
  routeTree,
  context: {
    auth: {
      isAuthenticated: false,
      profile: null,
      currentSessionId: null
    }
  },
  defaultErrorComponent: ErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  defaultPendingComponent: Loading
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export const App = () => {
  const queryClient = useQueryClient();
  const { locale } = useLocaleContext();
  const {
    profile,
    isAuthenticated,
    currentSessionId,
    profileDataIsLoading,
    updateProfile,
    updateprofileDataIsLoading,
    updateCurrentSessionId
  } = useProfileStore();

  usePushNotifications();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authToken = localStorage.getItem(
          APP_CONST.LOCAL_STORAGE.AUTH_TOKEN
        );
        if (authToken) {
          const profileData = await queryClient.fetchQuery({
            queryKey: ['initialProfileData'],
            queryFn: () => getProfiles({})
          });
          updateProfile(profileData.data);
          updateCurrentSessionId(profileData.data.profileIds[0]);
        }
        return true;
      } catch (e) {
        updateProfile(null);
        updateCurrentSessionId(null);
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.AUTH_TOKEN);
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.REFRESH_TOKEN);
        e;
      } finally {
        updateprofileDataIsLoading(false);
      }
    };

    if (!profile) {
      checkAuth();
    }
  }, []);

  const getTheme = () => {
    if (profile == null) {
      return DEFAULT_THEME;
    }
    return profile.role === Role.artist ? ARTIST_THEME : CLUB_THEME;
  };

  return (
    <PhotoProvider>
      <IntlProvider
        defaultLocale='en'
        locale={LOCALES[locale]}
        messages={messages[LOCALES[locale]]}
      >
        <MantineProvider withCssVariables theme={getTheme()}>
          <RootLayout>
            <Notifications
              autoClose={3000}
              containerWidth={320}
              limit={2}
              position='top-center'
            />
            {!profileDataIsLoading ? (
              <RouterProvider
                router={router}
                context={{
                  auth: {
                    isAuthenticated,
                    profile,
                    currentSessionId
                  }
                }}
              />
            ) : (
              <Loading />
            )}
          </RootLayout>
        </MantineProvider>
      </IntlProvider>
    </PhotoProvider>
  );
};
